import type { ShippingLocationMatch, ShippingRule } from '@vastra/types';

export function normalizeIndiaPincode(raw: string | undefined | null): string {
  return String(raw ?? '').replace(/\D/g, '').slice(0, 6);
}

export function normalizeStateKey(raw: string | undefined | null): string {
  return String(raw ?? '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
}

/** India checkout often stores `country: "India"`; shipping rules only match ISO `IN`. */
export function normalizeCountryCodeForShipping(raw: string | undefined | null): string {
  const t = String(raw ?? '').trim();
  if (!t) return 'IN';
  if (/^india$/i.test(t)) return 'IN';
  const u = t.toUpperCase();
  return u === 'IN' ? 'IN' : u;
}

function matchesPincodeRule(rule: ShippingRule, pin: string): boolean {
  const patterns = (rule.pincodes ?? []).map((p) => String(p).trim().replace(/\D/g, '')).filter(Boolean);
  if (patterns.length === 0) return false;
  for (const digits of patterns) {
    if (digits.length === 6) {
      if (pin === digits) return true;
    } else if (pin.startsWith(digits)) {
      return true;
    }
  }
  return false;
}

function matchesStateRule(rule: ShippingRule, stateKey: string): boolean {
  const list = (rule.states ?? []).map((s) => normalizeStateKey(s)).filter(Boolean);
  if (list.length === 0) return false;
  return list.includes(stateKey);
}

/** Used when pincode-based matching fails but the address includes a state that matches a rule. */
function matchShippingRuleByState(active: ShippingRule[], state: string | undefined): ShippingRule | null {
  const stateKey = normalizeStateKey(state);
  if (!stateKey) return null;
  const withStates = active.filter((r) => (r.states ?? []).some((s) => String(s).trim()));
  for (const r of withStates) {
    if (matchesStateRule(r, stateKey)) return r;
  }
  for (const r of active) {
    const list = (r.states ?? []).map((s) => normalizeStateKey(s)).filter(Boolean);
    if (list.length === 0) return r;
  }
  return null;
}

/**
 * Pick the first matching active rule: specific conditions before catch-all (empty states/pincodes).
 * `countryCode` not `IN` → no match.
 */
export function matchShippingRule(
  rules: ShippingRule[],
  locationMatch: ShippingLocationMatch,
  input: { countryCode?: string; state?: string; pincode?: string },
): ShippingRule | null {
  const countryCode = normalizeCountryCodeForShipping(input.countryCode);
  if (countryCode !== 'IN') return null;
  const active = rules.filter((r) => r.isActive !== false);

  if (locationMatch === 'pincode') {
    const pin = normalizeIndiaPincode(input.pincode);
    if (pin.length !== 6) {
      // Cart often has state but no full PIN — do not pick the first "empty pincodes" catch-all
      // (wrong zone / wrong free-shipping threshold). Prefer state-based match first, same as checkout
      // when PIN is filled and falls back to state.
      const byState = matchShippingRuleByState(active, input.state);
      if (byState) return byState;
      // Without state or full PIN, do not assume a catch-all zone (avoids showing "Free" shipping prematurely).
      if (!normalizeStateKey(input.state)) return null;
      for (const r of active) {
        const patterns = (r.pincodes ?? []).map((p) => String(p).trim()).filter(Boolean);
        if (patterns.length === 0) return r;
      }
      return null;
    }
    const withPatterns = active.filter((r) => (r.pincodes ?? []).some((p) => String(p).trim()));
    for (const r of withPatterns) {
      if (matchesPincodeRule(r, pin)) return r;
    }
    for (const r of active) {
      const patterns = (r.pincodes ?? []).map((p) => String(p).trim()).filter(Boolean);
      if (patterns.length === 0) return r;
    }
    // PIN not in any list and no "all pincodes" rule: fall back to state so checkout stays aligned with cart when rules mix PIN + state coverage
    return matchShippingRuleByState(active, input.state);
  }

  const stateKey = normalizeStateKey(input.state);
  // Require an explicit state before matching (no catch-all when state is still "Select…" on cart/checkout).
  if (!stateKey) {
    return null;
  }
  const withStates = active.filter((r) => (r.states ?? []).some((s) => String(s).trim()));
  for (const r of withStates) {
    if (matchesStateRule(r, stateKey)) return r;
  }
  for (const r of active) {
    const list = (r.states ?? []).map((s) => normalizeStateKey(s)).filter(Boolean);
    if (list.length === 0) return r;
  }
  return null;
}

export function shippingAmountForRule(rule: ShippingRule | null, subtotal: number): number {
  if (!rule) return 0;
  if (subtotal >= (rule.freeShippingThreshold ?? 0)) return 0;
  return Math.max(0, Number(rule.baseRate) || 0);
}
